<template>
  <div class="landing-root">
    <!-- Header -->
    <header class="landing-header">
      <div class="header-inner">
        <div class="brand">
          <svg
            class="brand-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="4" width="18" height="17" rx="3" stroke="currentColor" stroke-width="1.8" fill="none" />
            <path d="M3 9h18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            <path d="M8 2v3M16 2v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            <circle cx="8.5" cy="14" r="1.2" fill="currentColor" />
            <circle cx="12" cy="14" r="1.2" fill="currentColor" />
            <circle cx="15.5" cy="14" r="1.2" fill="currentColor" />
          </svg>
          <span class="brand-name">timeful</span>
        </div>
        <nav class="header-nav">
          <router-link class="nav-link" :to="{ name: 'list' }">All Meetings</router-link>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <main class="hero">
      <div class="hero-badge">Personal Fork</div>
      <h1 class="hero-title">Schedule meetings,<br />no fuss.</h1>
      <p class="hero-sub">
        This is a self-hosted personal fork of the
        <a
          href="https://github.com/schej-it/timeful.app"
          target="_blank"
          rel="noopener noreferrer"
          class="link"
          >open-source Timeful project</a
        >, stripped down for private use — no ads, no accounts, no tracking.
      </p>
      <div class="hero-actions">
        <v-btn
          id="create-event-btn"
          class="btn-primary"
          dark
          large
          @click="newDialog = true"
        >
          + Create event
        </v-btn>
        <router-link class="btn-secondary" :to="{ name: 'list' }">
          View all meetings →
        </router-link>
      </div>
    </main>

    <!-- Feature pills -->
    <section class="features">
      <div class="pill">📅 Calendar integration</div>
      <div class="pill">🔒 No accounts needed</div>
      <div class="pill">🌍 Timezone-aware</div>
      <div class="pill">🚫 Zero ads</div>
    </section>

    <!-- Footer -->
    <footer class="landing-footer">
      <span>
        Fork of
        <a
          href="https://timeful.app"
          target="_blank"
          rel="noopener noreferrer"
          class="link"
          >timeful.app</a
        >
        · self-hosted
      </span>
      <router-link class="link" :to="{ name: 'privacy-policy' }">Privacy Policy</router-link>
    </footer>

    <!-- New event dialog -->
    <NewDialog v-model="newDialog" no-tabs />
  </div>
</template>

<script>
import NewDialog from "@/components/NewDialog.vue"

export default {
  name: "Landing",

  metaInfo: {
    title: "Timeful — Personal Fork",
  },

  components: {
    NewDialog,
  },

  data: () => ({
    newDialog: false,
  }),
}
</script>

<style scoped>
.landing-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f7f7f5;
  font-family: "Inter", "DM Sans", sans-serif;
}

/* ── Header ── */
.landing-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(247, 247, 245, 0.9);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #e5e5e3;
}
.header-inner {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #1a1a18;
  text-decoration: none;
}
.brand-icon {
  width: 22px;
  height: 22px;
  color: #00994c;
}
.brand-name {
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #1a1a18;
}
.header-nav {
  display: flex;
  gap: 1rem;
}
.nav-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a4a47;
  text-decoration: none;
  padding: 0.3rem 0.75rem;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
}
.nav-link:hover {
  background: #eaeae8;
  color: #1a1a18;
}

/* ── Hero ── */
.hero {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 5rem 1.5rem 3rem;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}
.hero-badge {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00994c;
  background: #e6f5ed;
  border: 1px solid #b3dfc5;
  padding: 0.28rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 1.5rem;
}
.hero-title {
  font-size: clamp(2.4rem, 6vw, 3.8rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.035em;
  color: #1a1a18;
  margin-bottom: 1.25rem;
}
.hero-sub {
  font-size: 1.05rem;
  line-height: 1.65;
  color: #5a5a57;
  max-width: 500px;
  margin-bottom: 2.5rem;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}
.btn-primary {
  background-color: #00994c !important;
  color: #fff !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  padding: 0 2rem !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 2px 12px rgba(0, 153, 76, 0.35) !important;
  text-transform: none !important;
}
.btn-primary:hover {
  background-color: #007a3d !important;
}
.btn-secondary {
  font-size: 0.9rem;
  font-weight: 500;
  color: #4a4a47;
  text-decoration: none;
  padding: 0.55rem 1.2rem;
  border: 1px solid #d5d5d2;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.15s, color 0.15s, box-shadow 0.15s;
  line-height: 1;
}
.btn-secondary:hover {
  border-color: #aaa;
  color: #1a1a18;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
}

/* ── Feature pills ── */
.features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: center;
  padding: 0 1.5rem 4rem;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}
.pill {
  font-size: 0.83rem;
  font-weight: 500;
  color: #4a4a47;
  background: #fff;
  border: 1px solid #e2e2df;
  padding: 0.4rem 1rem;
  border-radius: 999px;
}

/* ── Footer ── */
.landing-footer {
  text-align: center;
  padding: 1.25rem 1.5rem;
  font-size: 0.8rem;
  color: #9a9a97;
  border-top: 1px solid #e5e5e3;
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}
.link {
  color: #00994c;
  text-decoration: none;
}
.link:hover {
  text-decoration: underline;
}
</style>
